import { RefObject, useEffect } from 'react'

type UseFloatingGradeNumbersOptions = {
  wrapperRef: RefObject<HTMLElement | null>
  rowSelector?: string
  labelSelector?: string
  trigger?: unknown
  debugKey?: string
  debugEnabled?: boolean
}

export const useFloatingGradeNumbers = ({
  wrapperRef,
  rowSelector = 'tbody tr',
  labelSelector = '.size-label',
  trigger,
  debugKey = 'default',
  debugEnabled = false,
}: UseFloatingGradeNumbersOptions) => {
  useEffect(() => {
    const wrapper = wrapperRef.current
    if (!wrapper) return

    const debugPrefix = `[useFloatingGradeNumbers:${debugKey}]`
    const logDebug = (...args: unknown[]) => {
      if (!debugEnabled) return
      console.log(debugPrefix, ...args)
    }

    const describeElement = (element: HTMLElement | null) => {
      if (!element) return null
      const style = window.getComputedStyle(element)
      return {
        tag: element.tagName,
        id: element.id || null,
        className: element.className || null,
        overflowY: style.overflowY,
        overflowX: style.overflowX,
        position: style.position,
        scrollTop: element.scrollTop,
        clientHeight: element.clientHeight,
        scrollHeight: element.scrollHeight,
      }
    }

    const getAncestorChain = (element: HTMLElement) => {
      const chain: Array<Record<string, unknown>> = []
      let current: HTMLElement | null = element
      let depth = 0
      while (current && depth < 16) {
        chain.push({ depth, ...describeElement(current) })
        current = current.parentElement
        depth += 1
      }
      return chain
    }

    const findScrollableAncestor = (element: HTMLElement): HTMLElement | null => {
      let current = element.parentElement
      while (current) {
        const style = window.getComputedStyle(current)
        const overflowY = style.overflowY
        const canScroll =
          (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') &&
          current.scrollHeight > current.clientHeight
        if (canScroll) return current
        current = current.parentElement
      }
      return null
    }

    const wrapperOwnsScroll = wrapper.scrollHeight > wrapper.clientHeight
    const ancestorScrollHost = findScrollableAncestor(wrapper)
    const scrollHost = wrapperOwnsScroll ? wrapper : ancestorScrollHost

    logDebug('init', {
      wrapper: describeElement(wrapper),
      scrollHost: describeElement(scrollHost),
      wrapperOwnsScroll,
      ancestorChain: getAncestorChain(wrapper),
    })

    let debugEventCount = 0

    const onScroll = (source: string) => {
      const rows = Array.from(wrapper.querySelectorAll<HTMLTableRowElement>(rowSelector))
      if (rows.length === 0) return

      const wrapperRect = wrapper.getBoundingClientRect()
      const scrollHostRect = scrollHost ? scrollHost.getBoundingClientRect() : null
      const topReference = scrollHost && scrollHost !== wrapper
        ? Math.max(wrapperRect.top, scrollHostRect ? scrollHostRect.top : wrapperRect.top)
        : wrapperRect.top
      const rawScrollTop = scrollHost ? scrollHost.scrollTop : wrapper.scrollTop
      const rowMetrics = rows.map((row, idx) => {
        const rect = row.getBoundingClientRect()
        const height = rect.height || row.offsetHeight || 0
        const visible = rect.bottom > wrapperRect.top && rect.top < wrapperRect.bottom
        const intersectsTopLine = rect.top <= topReference && rect.bottom > topReference
        return {
          idx,
          top: rect.top,
          bottom: rect.bottom,
          height,
          visible,
          intersectsTopLine,
          distanceToTopLine: Math.abs(rect.top - topReference),
        }
      })

      const topIntersectingRow = rowMetrics.find((metric) => metric.intersectsTopLine)
      const firstVisibleRow = rowMetrics.find((metric) => metric.visible)
      const nearestRow = rowMetrics.reduce((best, current) => {
        if (!best || current.distanceToTopLine < best.distanceToTopLine) return current
        return best
      }, null as null | {
        idx: number
        top: number
        bottom: number
        height: number
        visible: boolean
        intersectsTopLine: boolean
        distanceToTopLine: number
      })

      const activeRowMetric = topIntersectingRow || firstVisibleRow || nearestRow || rowMetrics[0]
      const activeRowIndex = activeRowMetric.idx
      const maxTranslate = Math.max(0, activeRowMetric.height)
      const overScroll = Math.max(0, Math.min(topReference - activeRowMetric.top, maxTranslate))

      debugEventCount += 1
      if (debugEnabled && (debugEventCount <= 50 || debugEventCount % 25 === 0)) {
        logDebug('onScroll', {
          source,
          eventCount: debugEventCount,
          rowCount: rows.length,
          rawScrollTop,
          wrapperScrollTop: wrapper.scrollTop,
          wrapperRectTop: wrapperRect.top,
          wrapperRectBottom: wrapperRect.bottom,
          topReference,
          activeRowIndex,
          activeRowTop: activeRowMetric.top,
          activeRowBottom: activeRowMetric.bottom,
          activeRowHeight: activeRowMetric.height,
          intersectsTopLine: activeRowMetric.intersectsTopLine,
          overScroll,
          scrollHostRectTop: scrollHostRect ? scrollHostRect.top : null,
        })
      }

      rows.forEach((row, idx) => {
        const labels = Array.from(row.querySelectorAll<HTMLElement>(labelSelector))
        if (idx === activeRowIndex) {
          labels.forEach((label) => {
            label.style.setProperty('visibility', 'visible', 'important')
            label.style.transform = `translateY(${overScroll}px)`
            label.style.zIndex = '10'
          })
        } else {
          labels.forEach((label) => {
            label.style.setProperty('visibility', 'hidden', 'important')
            label.style.transform = 'translateY(0px)'
            label.style.zIndex = '1'
          })
        }
      })
    }

    const onWrapperScroll = () => onScroll('wrapper')
    const onHostScroll = () => onScroll('host')
    const onWindowScroll = () => onScroll('window')
    const onWindowResize = () => onScroll('resize')

    wrapper.addEventListener('scroll', onWrapperScroll, { passive: true })
    if (scrollHost && scrollHost !== wrapper) {
      scrollHost.addEventListener('scroll', onHostScroll, { passive: true })
    }
    window.addEventListener('scroll', onWindowScroll, { passive: true })
    window.addEventListener('resize', onWindowResize, { passive: true })

    const observer = new MutationObserver(() => {
      onScroll('mutation')
    })
    observer.observe(wrapper, { childList: true, subtree: true })

    onScroll('init')

    return () => {
      wrapper.removeEventListener('scroll', onWrapperScroll)
      if (scrollHost && scrollHost !== wrapper) {
        scrollHost.removeEventListener('scroll', onHostScroll)
      }
      window.removeEventListener('scroll', onWindowScroll)
      window.removeEventListener('resize', onWindowResize)
      observer.disconnect()
    }
  }, [wrapperRef, rowSelector, labelSelector, trigger, debugKey, debugEnabled])
}
